import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ApiError } from '../api/client'
import { useAuth } from '../auth/useAuth'
import { AttachChecklist } from '../components/AttachChecklist'
import { PageMessage } from '../components/PageMessage'
import { PlanView } from '../components/PlanView'
import { useDeletePlan, usePlan, usePlanDeletionPreview } from '../features/plans/api'
import {
  EVENT_STATUSES,
  formatDateRange,
  useChangeEventStatus,
  useEvent,
  useSetEventArchived,
  type EventStatus,
} from '../features/events/api'

export function EventDetailPage() {
  const { eventId } = useParams()
  const { can } = useAuth()
  const { data: event, isPending, isError, error } = useEvent(eventId)
  const changeStatus = useChangeEventStatus(eventId ?? '')
  const setArchived = useSetEventArchived(eventId ?? '')
  const [actionError, setActionError] = useState<string | null>(null)
  const { data: plan } = usePlan(eventId)
  const deletionPreview = usePlanDeletionPreview(eventId ?? '')
  const deletePlan = useDeletePlan(eventId ?? '')
  const [pendingDeletion, setPendingDeletion] = useState<string | null>(null)

  if (isPending) {
    return <p>Loading event…</p>
  }

  if (isError || !event) {
    const notFound = error instanceof ApiError && error.status === 404
    return (
      <PageMessage
        title={notFound ? 'Event not found' : 'Could not load event'}
        body={notFound ? 'That event does not exist or has been deleted.' : undefined}
      >
        <Link to="/events">Back to events</Link>
      </PageMessage>
    )
  }

  const report = (err: unknown) =>
    setActionError(err instanceof ApiError ? err.detail : 'That change could not be applied.')

  return (
    <section>
      <div className="page-header">
        <div>
          <h1>{event.name}</h1>
          <div className="card-meta">
            <span className="code">{event.code}</span>
            <span>{formatDateRange(event.startDate, event.endDate)}</span>
            {event.venueName ? <span>{event.venueName}</span> : null}
            {event.venueCity ? <span>{event.venueCity}</span> : null}
          </div>
        </div>

        {can('Coordinator') ? (
          <Link className="button" to={`/events/${event.id}/edit`}>
            Edit
          </Link>
        ) : null}
      </div>

      {actionError ? (
        <p role="alert" className="error">
          {actionError}
        </p>
      ) : null}

      <div className="detail-grid">
        <div className="detail-item">
          <span className="detail-label">Account manager</span>
          <span>
            {event.accountManagerDisplayName ?? 'Unknown user'}
            {event.needsAccountManager ? (
              <strong className="overdue"> — no longer active, name a replacement</strong>
            ) : null}
          </span>
        </div>

        <div className="detail-item">
          <span className="detail-label">Status</span>
          {can('Coordinator') ? (
            <select
              aria-label="Event status"
              value={event.status}
              disabled={changeStatus.isPending}
              onChange={(e) => {
                setActionError(null)
                changeStatus.mutateAsync(e.target.value as EventStatus).catch(report)
              }}
            >
              {EVENT_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          ) : (
            <span className={`status status-${event.status.toLowerCase()}`}>{event.status}</span>
          )}
        </div>

        {event.venueTimeZoneId ? (
          <div className="detail-item">
            <span className="detail-label">Venue time zone</span>
            <span>{event.venueTimeZoneId}</span>
          </div>
        ) : null}

        {event.externalCustomRegId ? (
          <div className="detail-item">
            <span className="detail-label">CustomReg ID</span>
            <span>{event.externalCustomRegId}</span>
          </div>
        ) : null}

        {event.isArchived ? (
          <div className="detail-item">
            <span className="detail-label">Archived</span>
            <span>Hidden from default views</span>
          </div>
        ) : null}
      </div>

      {event.notes ? (
        <div className="notes">
          <h2>Notes</h2>
          <p>{event.notes}</p>
        </div>
      ) : null}

      {plan ? (
        <>
          <PlanView plan={plan} />
          {can('Coordinator') ? (
            <div className="panel">
              <h2>Checklists</h2>
              <AttachChecklist plan={plan} />
            </div>
          ) : null}
        </>
      ) : (
        <div className="panel">
          <h2>Plan</h2>
          <p className="muted">
            No plan yet. Compose one from a published timeline template, with any published
            checklist templates you need. Checklists can be added later.
          </p>
          {can('Coordinator') ? (
            <Link className="button primary" to={`/events/${event.id}/plan/new`}>
              Compose a plan
            </Link>
          ) : null}
        </div>
      )}

      {can('Admin') ? (
        <div className="panel danger-zone">
          <h2>Administration</h2>
          <button
            type="button"
            disabled={setArchived.isPending}
            onClick={() => {
              setActionError(null)
              setArchived.mutateAsync(!event.isArchived).catch(report)
            }}
          >
            {event.isArchived ? 'Unarchive event' : 'Archive event'}
          </button>
          <p className="hint">
            Archiving hides the event from default views. It never deletes the event or its plan.
          </p>

          {plan ? (
            <div className="danger-action">
              {pendingDeletion ? (
                <>
                  <p role="alert" className="warning">
                    {pendingDeletion}
                  </p>
                  <div className="form-actions">
                    <button
                      type="button"
                      disabled={deletePlan.isPending}
                      onClick={() => {
                        setActionError(null)
                        deletePlan
                          .mutateAsync()
                          .then(() => setPendingDeletion(null))
                          .catch(report)
                      }}
                    >
                      Yes, delete the plan
                    </button>
                    <button type="button" onClick={() => setPendingDeletion(null)}>
                      Keep it
                    </button>
                  </div>
                </>
              ) : (
                <button
                  type="button"
                  disabled={deletionPreview.isPending}
                  onClick={async () => {
                    setActionError(null)
                    try {
                      const preview = await deletionPreview.mutateAsync()
                      setPendingDeletion(
                        `Deleting this plan destroys ${preview.completedCount} completed item(s), ` +
                          `${preview.attachmentCount} attachment(s), and ${preview.noteCount} note(s). ` +
                          'This cannot be undone.',
                      )
                    } catch (err) {
                      report(err)
                    }
                  }}
                >
                  Delete plan
                </button>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  )
}
